import {
  buildCompareData,
  getDefaultSelectedProducts,
  setupStickyHeader,
  mapAllSpecs,
} from '//robodev10.myshopify.com/cdn/shop/t/4/assets/roborock-compare.js?v=99369833393653741911786354223';

const MOBILE_BREAKPOINT = 767;
const compareApps = new Map();
const compareStates = new Map();
const embedAssetUrl = '//robodev10.myshopify.com/cdn/shop/t/4/assets/product-compare-embed.js?v=57221325030373501001784184997';

function readCompareConfig(container) {
  try {
    return {
      local: container.dataset.local,
      unitSystem: container.dataset.unitSystem,
      compareType: container.dataset.compareType,
      visibleSpecConfig: JSON.parse(container.dataset.visibleSpecConfig || '[]'),
      enableEmbed: container.dataset.compareEmbed === 'true',
      embedTarget: container.dataset.embedTarget,
    };
  } catch (err) {
    console.error('[roborock-compare] invalid config JSON:', err);
    return null;
  }
}

function destroyCompareSection(sectionId, nextContainer) {
  const entry = compareApps.get(sectionId);
  if (!entry) return;
  entry.app.unmount();
  if (entry.container !== nextContainer) {
    entry.container.remove();
  }
  compareApps.delete(sectionId);
}

function createCompareApp(config, sectionId) {
  const { createApp, ref, shallowRef, reactive, useTemplateRef, computed, watch, nextTick } = Vue;

  return createApp({
    delimiters: ['[[', ']]'],
    setup() {
      const stickyContainer = useTemplateRef('CompareSelectorContainer');
      nextTick(() => {
        setupStickyHeader(stickyContainer.value);
      });

      const productInfo = buildCompareData();
      const productSeries = shallowRef(productInfo.sortedProducts);
      const displayCount = computed(() => {
        const isMobile = MOBILE_BREAKPOINT > window.innerWidth;
        if (isMobile) return 2;
        return productInfo.allProducts.length > 2 ? 3 : 2;
      });

      const savedState = compareStates.get(sectionId);
      const productById = new Map(productInfo.allProducts.map((product) => [product.productId, product]));
      const restoredProducts = savedState?.selectedProductIds
        .map((productId) => productById.get(productId))
        .filter(Boolean);
      const defaultProducts = getDefaultSelectedProducts(productInfo.allProducts, displayCount.value);
      const selectedProductList = reactive(restoredProducts?.length ? restoredProducts : defaultProducts);
      const currentSeletorIndex = ref(-1);
      const useDiffFilter = ref(savedState?.useDiffFilter || false);
      const remarkList = ref([]);
      const filteredSpecGroups = ref([]);

      function closeDropdowns() {
        currentSeletorIndex.value = -1;
      }

      function mouseleaveDropdowns() {
        setTimeout(closeDropdowns, 300);
      }

      function toggleDropdown(e, index) {
        if (index - 1 === currentSeletorIndex.value) {
          closeDropdowns();
        } else {
          currentSeletorIndex.value = index - 1;
          const selector = e.target.closest('.product-selector');
          const dropdown = selector?.querySelector('.selector-dropdown');
          if (dropdown) {
            const trigger = e.target.closest('.selector-trigger');
            const triggerRect = trigger.getBoundingClientRect();
            const spaceBelow = window.innerHeight - triggerRect.bottom;
            dropdown.style.maxHeight = `${spaceBelow - 20}px`;
          }
        }
      }

      function toggleProductGroup(event) {
        event.target.closest('.group-header')?.classList.toggle('active');
      }

      function selectProduct(product) {
        const index = currentSeletorIndex.value;
        const newProductId = product.productId;
        const oldProduct = selectedProductList[index];
        if (!oldProduct || newProductId !== oldProduct.productId) {
          const existingIndex = selectedProductList.findIndex((p) => p && p.productId === newProductId);
          if (existingIndex !== -1) {
            selectedProductList[existingIndex] = oldProduct;
          }
          selectedProductList[index] = product;
        }
        closeDropdowns();
      }

      watch(
        [() => selectedProductList.map((p) => p?.productSlug), useDiffFilter],
        async ([selectedProductSlugs]) => {
          compareStates.set(sectionId, {
            selectedProductIds: selectedProductList.map((product) => product?.productId).filter(Boolean),
            useDiffFilter: useDiffFilter.value,
          });
          const { groups: specGroupMapped, remarkSet } = await mapAllSpecs({
            local: config.local,
            unitSystem: config.unitSystem,
            compareType: config.compareType,
            selectedProductSlugs,
            visibleSpecConfig: config.visibleSpecConfig,
          });
          remarkList.value = Array.from(remarkSet);
          filteredSpecGroups.value = specGroupMapped
            .map((group) => {
              const specs = group.specs.filter((spec) => {
                const valuesForSelected = spec.values.map(
                  (v) => v.compareText ?? v.largeText + v.standardText + v.descText
                );
                if (valuesForSelected.every((s) => s === '' || s === '-')) return false;
                if (!useDiffFilter.value) return true;
                const first = valuesForSelected[0];
                return !valuesForSelected.every((v) => v === first);
              });
              return { ...group, specs };
            })
            .filter((group) => group.specs.length > 0);
        },
        { immediate: true }
      );

      return {
        productSeries,
        displayCount,
        useDiffFilter,
        selectedProductList,
        currentSeletorIndex,
        mouseleaveDropdowns,
        toggleDropdown,
        toggleProductGroup,
        selectProduct,
        filteredSpecGroups,
        remarkList,
      };
    },
  });
}

function runEmbed(config, container, sectionId) {
  if (!config.enableEmbed || !config.embedTarget) return;
  import(embedAssetUrl)
    .then(({ embedProductCompare }) => {
      const currentEntry = compareApps.get(sectionId);
      if (currentEntry?.container === container) {
        embedProductCompare(config.embedTarget, container);
      }
    })
    .catch((err) => console.error('[product-compare] embed failed:', err));
}

function initCompareSection(container) {
  if (!container || !window.Vue) return;

  const sectionId = container.dataset.sectionId;
  if (!sectionId) return;

  const existingEntry = compareApps.get(sectionId);
  if (existingEntry?.container === container) return;

  const config = readCompareConfig(container);
  if (!config) return;

  destroyCompareSection(sectionId, container);

  const homeContainer = container.parentElement;
  const app = createCompareApp(config, sectionId);
  app.mount(container);
  compareApps.set(sectionId, { app, config, container, homeContainer });
  runEmbed(config, container, sectionId);
}

function mountAllCompareSections(root = document) {
  root.querySelectorAll('[data-roborock-compare]').forEach(initCompareSection);
}

function reconcileEmbeddedSections() {
  for (const [sectionId, entry] of compareApps) {
    const { config, container, homeContainer } = entry;
    if (!config.enableEmbed || !config.embedTarget) continue;

    const placeholder = document.getElementById(config.embedTarget);
    if (placeholder) {
      if (container.parentElement !== placeholder) {
        runEmbed(config, container, sectionId);
      }
    } else if (homeContainer?.isConnected && container.parentElement !== homeContainer) {
      homeContainer.appendChild(container);
    }
  }
}

function waitForVue() {
  if (window.Vue) return Promise.resolve();
  return new Promise((resolve) => {
    const check = () => {
      if (window.Vue) resolve();
      else requestAnimationFrame(check);
    };
    check();
  });
}

function handleSectionLoad(event) {
  waitForVue().then(() => {
    const container = event.target.matches?.('[data-roborock-compare]')
      ? event.target
      : event.target.querySelector('[data-roborock-compare]');
    if (container) {
      initCompareSection(container);
    } else {
      reconcileEmbeddedSections();
    }
  });
}

function handleSectionUnload(event) {
  destroyCompareSection(event.detail.sectionId);
}

let listenersBound = false;

function bindThemeEditorListeners() {
  if (listenersBound) return;
  listenersBound = true;
  document.addEventListener('shopify:section:load', handleSectionLoad);
  document.addEventListener('shopify:section:unload', handleSectionUnload);
}

waitForVue().then(() => {
  mountAllCompareSections();
  bindThemeEditorListeners();
});
